// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'tiny... Remove this comment to see the full error message
import tinymce from 'tinymce';
import I18n from 'I18n';
import { Link } from 'foundations-components/transitional/components/Link';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { compose, curry } from 'rich-text-lib/utils/iterables';
import { i18nNameSpace, hsformattingConfig, UnsupportedFonts, FORMATTING_SETTINGS } from '../constants/hsformatting';
import { CHANGE_FONT } from '../constants/usageTracking';
import { addPreProcessTransform, addPostProcessTransform } from '../utils/transformer';
import { trackInteraction } from '../utils/usageTracking';
import { maybeGetComputedStyle } from './getComputedStyle';
import { getIsInPage, getPluginSetting } from '../utils';
import { jsx as _jsx } from "react/jsx-runtime";
const CLEAN_FONT_STACK_QUOTES_REGEXP = /['"]/g;
const CLEAN_FONT_STACK_COMMAS_REGEXP = /(, )/g;
const split = curry((separator, str) => str.split(separator));
const normalizeFontStack = fontStack => {
  return fontStack.replace(CLEAN_FONT_STACK_QUOTES_REGEXP, '').replace(CLEAN_FONT_STACK_COMMAS_REGEXP, ',').toLowerCase().trim();
};
const normalizeAndFlattenFontValue = compose(split(','), normalizeFontStack);
export const matchFont = (computedFontFamily, editorFonts) => {
  const computedFontFamilyPrimary = normalizeAndFlattenFontValue(computedFontFamily)[0];
  return editorFonts.find(fontFamily => {
    const fontFamilyPrimary = normalizeFontStack(fontFamily.value).split(',')[0];
    return computedFontFamilyPrimary === fontFamilyPrimary;
  });
};
export const getCustomFontSelectOptions = customFonts => {
  return customFonts.map(font => {
    const name = font.family || font.name || '';
    return Object.assign({}, font, {
      value: name + (font.fallback ? `, ${font.fallback}` : ''),
      family: name
    });
  });
};
export const getFontSelectOptions = fonts => {
  if (!fonts) {
    return [];
  }
  const unsupportedFontLink = /*#__PURE__*/_jsx(Link, {
    external: true,
    href: "https://blog.hubspot.com/website/web-safe-html-css-fonts",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `${i18nNameSpace}.fontFamily.unsupportedFontLink`
    })
  });
  const help = fontName => /*#__PURE__*/_jsx(FormattedReactMessage, {
    message: `${i18nNameSpace}.fontFamily.unsupportedFontErrorText`,
    options: {
      fontName,
      unsupportedFontLink
    }
  });
  return Object.keys(fonts).sort().map(font => {
    const disabled = UnsupportedFonts.includes(fonts[font]);
    return Object.assign({
      text: font,
      value: fonts[font],
      disabled
    }, disabled ? {
      help: help(font)
    } : {});
  });
};
export const parseFonts = (editor, fonts, useParsedFonts) => {
  const unknownFonts = [];
  const usedUnsupportedFonts = [];

  // Should we parse the entire document
  const element = editor.settings.inline ? editor.getElement() : editor.getBody();
  const flatFonts = fonts.reduce((acc, value) => {
    return [/* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
    ...acc, ...normalizeAndFlattenFontValue(value)];
  }, []);
  const walker = new tinymce.dom.TreeWalker(element);
  do {
    const current = walker.current();
    if (current.nodeType !== 1) {
      continue;
    }
    const fontFamily = getComputedStyle(current).fontFamily;
    const normalFlatFontFamilyPrimary = normalizeAndFlattenFontValue(fontFamily)[0];
    if (UnsupportedFonts.includes(normalFlatFontFamilyPrimary)) {
      usedUnsupportedFonts.push(normalFlatFontFamilyPrimary);
      continue;
    }
    if (unknownFonts.includes(fontFamily) || flatFonts.includes(normalFlatFontFamilyPrimary)) {
      continue;
    }
    if (useParsedFonts) {
      unknownFonts.push(fontFamily);
    }
  } while (element.contains(walker.next()));
  return {
    unknownFonts,
    usedUnsupportedFonts
  };
};
export const calculateParsedFonts = (editor, useParsedFonts, fonts, webfonts) => {
  const {
    unknownFonts = [],
    usedUnsupportedFonts
  } = parseFonts(editor, [...fonts.map(({
    value: fontValue
  }) => fontValue), ...webfonts.map(({
    value: webFontValue
  }) => webFontValue)], useParsedFonts);
  const fontsWithUsedUnsupportedFonts = fonts.filter(({
    value
  }) => {
    if (UnsupportedFonts.includes(value)) {
      return usedUnsupportedFonts.includes(value);
    }
    return true;
  });
  return {
    parsedFonts: unknownFonts.map(unknownFontValue => ({
      value: unknownFontValue,
      text: unknownFontValue.split(',')[0].replace(/['"]/g, '')
    })),
    fontsWithUsedUnsupportedFonts
  };
};
export const constructParsedFontText = isEmail => ({
  help: I18n.text(`${i18nNameSpace}.fontFamily.${isEmail ? 'email' : 'pages'}.templateFontTooltip`),
  text: I18n.text(`${i18nNameSpace}.fontFamily.templateFontGroupName`)
});
export const getHandleChangeFont = editor => (fontOption, isCustomFont) => {
  const valueToUse = isCustomFont ? fontOption : fontOption.value;
  const customFontType = isCustomFont ? fontOption.fontValue && fontOption.fontValue.font_set : undefined;
  editor.execCommand('hsfontselect', false, {
    value: valueToUse,
    isCustomFont
  });
  trackInteraction(editor, 'tinymceFormatting', CHANGE_FONT, {
    method: 'button',
    valueToUse,
    isCustomFont,
    customFontType
  });
};
const createNodeChangeHandler = (handlerFunc, inherits = false) => {
  return e => {
    const {
      element,
      target,
      parents
    } = e;
    const win = target.getWin();
    let computedStyle = maybeGetComputedStyle(win, element) || {};
    const elementHasStyle = handlerFunc(computedStyle);
    if (!elementHasStyle && inherits) {
      //some styles will inherit an active style from the parent no matter what (underline)
      //check all the parents to see if they have the style even though the child doesn't
      return !!parents.find(parent => {
        computedStyle = maybeGetComputedStyle(win, parent) || {};
        return handlerFunc(computedStyle);
      });
    }
    return elementHasStyle;
  };
};
const getLineHeightDisplayValue = (win, element) => {
  const computedStyle = maybeGetComputedStyle(win, element);
  if (!computedStyle) {
    return null;
  }
  const computedLineHeight = computedStyle.getPropertyValue('line-height');
  const computedFontSize = computedStyle.getPropertyValue('font-size');
  const parsedLineHeight = parseFloat(computedLineHeight);
  const parsedFontSize = parseFloat(computedFontSize);
  if (!isNaN(parsedLineHeight) && !isNaN(parsedFontSize)) {
    return parsedLineHeight / parsedFontSize;
  }
  return null;
};
export const nodeChangeHandlers = {
  bold: createNodeChangeHandler(computedStyle => {
    if (computedStyle.fontWeight === 'bold' || Number(computedStyle.fontWeight) >= 600) {
      return true;
    }
    return false;
  }),
  underline: createNodeChangeHandler(computedStyle => {
    return computedStyle.textDecoration.includes('underline');
  }, true),
  italic: createNodeChangeHandler(computedStyle => {
    return computedStyle.fontStyle.includes('italic');
  })
};
export const overrideInheritedStyles = {
  bold: {
    on: 'bold',
    off: 'normal',
    affectedStyles: ['font-weight'],
    inlineElements: ['b', 'strong'],
    tinyMCECommand: 'bold'
  },
  italic: {
    on: 'italic',
    off: 'normal',
    affectedStyles: ['font-style'],
    inlineElements: ['i', 'em'],
    tinyMCECommand: 'italic'
  },
  underline: {
    on: 'underline',
    off: 'none',
    affectedStyles: ['text-decoration'],
    inlineElements: ['u'],
    tinyMCECommand: 'underline'
  }
};
export const getMatchingValueHandler = (editor, format, values) => nodeChangeEvent => {
  const {
    parents,
    element
  } = nodeChangeEvent;
  const {
    formatter
  } = editor;
  let newControlValue;
  for (let i = 0; i < parents.length; i++) {
    const matchingItem = values.find(item => formatter.matchNode(parents[i], format, {
      value: item.value
    }));
    if (matchingItem) {
      newControlValue = matchingItem.value;
      break;
    }
  }
  if (!newControlValue) {
    const maybeLineHeightDisplayValue = getLineHeightDisplayValue(editor.getWin(), element);
    const matched = values.find(item => item.value === maybeLineHeightDisplayValue);
    newControlValue = matched ? matched.value : null;
  }
  return newControlValue;
};
export const getListNode = (editor, selectedNode) => {
  if (!selectedNode) return null;
  return editor.dom.getParent(selectedNode, 'li', editor.getBody());
};
export const isListParentNode = selectedNode => {
  if (!selectedNode) return null;
  return ['OL', 'UL'].includes(selectedNode.nodeName);
};
export const customFontFunctionHublRegex = new RegExp(/{{\s+include_custom_fonts((.*?))\s+}}/g);
const createCustomFontsHubl = (customFonts, usedCustomFonts) => {
  const hublFonts = {};
  if (!usedCustomFonts.length) {
    return '';
  }
  usedCustomFonts.forEach(customFontName => {
    const currentCustomFont = customFonts.find(customFont => customFontName === customFont.family);
    if (!currentCustomFont) return;
    const {
      variants
    } = currentCustomFont;
    if (!variants || !variants.length) return;

    // there are currently 2 types of custom fonts - dev added theme and marketer added branding
    // developer added theme custom fonts have variants as type string[]
    // marketer uploaded brand custom fonts have variants as type { name: string, ... }
    if (typeof variants[0] === 'string') {
      hublFonts[customFontName] = currentCustomFont.variants;
    } else if (variants[0].name) {
      hublFonts[customFontName] = variants.map(variant => variant.name);
    }
  });
  return `{{ include_custom_fonts(${JSON.stringify(hublFonts)}) }}`;
};
const parseUsedCustomFonts = (editor, customFonts) => {
  const usedSupportedCustomFonts = new Set();
  const element = editor.settings.inline ? editor.getElement() : editor.getBody();
  const walker = new tinymce.dom.TreeWalker(element);
  do {
    const current = walker.current();
    if (current.nodeType !== 1) {
      continue;
    }
    const fontFamily = getComputedStyle(current).fontFamily;
    const matchedFont = matchFont(fontFamily, customFonts);
    if (matchedFont) {
      usedSupportedCustomFonts.add(matchedFont.family);
    }
  } while (element.contains(walker.next()));
  return Array.from(usedSupportedCustomFonts);
};
export const getCustomFonts = editor => {
  const {
    [hsformattingConfig.FONT_FORMATS]: fontFormats
  } = getPluginSetting(editor, FORMATTING_SETTINGS);
  return getCustomFontSelectOptions(fontFormats.customFonts || []);
};
export const getCombinedCustomFonts = editor => {
  var _editor$plugins$hsfor, _editor$plugins$hsfor2;
  return (_editor$plugins$hsfor = (_editor$plugins$hsfor2 = editor.plugins.hsformatting) === null || _editor$plugins$hsfor2 === void 0 ? void 0 : _editor$plugins$hsfor2.combinedCustomFonts) !== null && _editor$plugins$hsfor !== void 0 ? _editor$plugins$hsfor : [];
};
export const getBrandingFonts = editor => {
  var _editor$plugins$hsfor3, _editor$plugins$hsfor4;
  return (_editor$plugins$hsfor3 = (_editor$plugins$hsfor4 = editor.plugins.hsformatting) === null || _editor$plugins$hsfor4 === void 0 ? void 0 : _editor$plugins$hsfor4.brandingFonts) !== null && _editor$plugins$hsfor3 !== void 0 ? _editor$plugins$hsfor3 : [];
};
function generateFontFaceSrc(files) {
  return files.map(fileInfo => `url('${fileInfo.file}') format('${fileInfo.format}')`).join(', ');
}
const FONT_FACE_PROPERTIES_ALLOWED = ['font-weight', 'font-style'];
function generateFontFaceProperties(style) {
  return Object.entries(style).filter(([key]) => FONT_FACE_PROPERTIES_ALLOWED.includes(key)).map(([key, value]) => {
    return `${key}: ${value};`;
  }).join(' ');
}
function generateFontFaceDeclaration(fontFamily, variant) {
  if (typeof variant === 'string') {
    return '';
  }
  return `
    @font-face {
      font-family: '${fontFamily}';
      src: ${generateFontFaceSrc(variant.files)};
      font-display: swap;
      ${generateFontFaceProperties(JSON.parse(variant.style))}
    }`;
}
export function injectCustomFontFaceDeclarations(editor, customFonts = getCombinedCustomFonts(editor)) {
  // Don't inject custom font face declarations for inpage editors. Inpage
  // editors should pull added custom fonts upon save, but this check can be
  // removed in the future for a snappier UX.
  if (getIsInPage(editor.settings)) {
    return;
  }
  if (customFonts.length === 0) {
    return;
  }
  try {
    const editorDoc = editor.getDoc();
    const fontStyle = editorDoc.createElement('style');
    const elementId = 'custom-font-face-declarations';
    fontStyle.id = elementId;
    const styleText = customFonts.flatMap(font => font.variants.map(variant => [font.family, variant])).filter(([__, variant]) => typeof variant !== 'string').map(([font, variant]) => generateFontFaceDeclaration(font, variant)).join('');
    fontStyle.textContent = styleText;
    const existingFontDeclaration = editorDoc.getElementById(elementId);
    if (existingFontDeclaration) {
      existingFontDeclaration.replaceWith(fontStyle);
    } else {
      editorDoc.head.append(fontStyle);
    }
  } catch (err) {
    console.error('Error creating custom font face declarations', err);
  }
}
export const appendCustomFontsHublToContent = (editor, content, customFonts) => {
  const fontsUsed = parseUsedCustomFonts(editor, customFonts);
  const customFontsHubl = createCustomFontsHubl(customFonts, fontsUsed);
  return content + customFontsHubl;
};
export const extractCustomFontsHublFromContent = domContent => {
  let content = domContent;
  let hublCustomFontsTagsLeftInDomContent = customFontFunctionHublRegex.exec(content);
  while (hublCustomFontsTagsLeftInDomContent !== null) {
    if (hublCustomFontsTagsLeftInDomContent.length) {
      content = content.replace(hublCustomFontsTagsLeftInDomContent[0], '');
    }
    hublCustomFontsTagsLeftInDomContent = customFontFunctionHublRegex.exec(content);
  }
  return content;
};
export const addCustomFontPreProcessTransform = editor => {
  addPreProcessTransform(editor, dom => {
    dom.body.innerHTML = extractCustomFontsHublFromContent(dom.body.innerHTML);
  });
};
export const addCustomFontPostProcessTransform = (editor, customFonts) => {
  const combinedCustomFonts = customFonts || getCombinedCustomFonts(editor);
  addPostProcessTransform(editor, dom => {
    dom.body.innerHTML = appendCustomFontsHublToContent(editor, dom.body.innerHTML, combinedCustomFonts);
  });
};